import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import { useAdminAuth } from "auth/AdminAuthProvider";
import { adminRequest } from "services/adminApi";

const fallback = {
  countries: 0,
  cities: 88,
  destinations: 88,
  pendingDrafts: 0,
  incompleteDestinations: 0,
  activeNotices: 0,
  activeUsers: 0,
  currentTrips: 0,
  recentAudit: [],
};

function Dashboard() {
  const auth = useAdminAuth();
  const [data, setData] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    adminRequest("/admin/dashboard")
      .then(setData)
      .catch((requestError) => {
        setError(requestError.message);
        setData(fallback);
      });
    adminRequest("/admin/analytics?days=30&platform=all")
      .then(setGrowth)
      .catch(() => setGrowth(null));
  }, []);

  if (!data) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox minHeight="60vh" display="flex" alignItems="center" justifyContent="center">
          <CircularProgress color="success" />
        </MDBox>
      </DashboardLayout>
    );
  }

  const cards = [
    ["الدول المنشورة", data.countries ?? data.destinations, "public", "language", "/countries"],
    ["المدن المنشورة", data.cities ?? data.destinations, "info", "location_city", "/countries"],
    ["مسودات تحتاج مراجعة", data.pendingDrafts, "warning", "edit_note", "/countries"],
    ["وجهات تحتاج إكمال", data.incompleteDestinations, "error", "rule", "/countries"],
    ["تنبيهات فعّالة", data.activeNotices, "success", "campaign", "/notices"],
    ["حسابات مفعّلة", data.activeUsers, "info", "groups", "/users"],
    ["رحلات جارية", data.currentTrips, "dark", "flight_takeoff", "/trips"],
  ];
  const quickActions = [
    {
      title: "إضافة دولة",
      description: "أضف معلومات الدولة أولاً، ثم أنشئ المدن والأماكن داخلها.",
      icon: "add_location_alt",
      to: "/countries",
    },
    {
      title: "إنشاء تنبيه سفر",
      description: "أنشئ تنبيهاً عاماً أو مخصصاً لدولة أو مدينة.",
      icon: "campaign",
      to: "/notices?new=1",
    },
    ...(auth.canPublish
      ? [
          {
            title: "رسائل المسافرين",
            description: "راجع الرسائل الجديدة وحدّث حالة المتابعة.",
            icon: "mail",
            to: "/contact-messages",
          },
        ]
      : []),
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <MDTypography variant="h4" fontWeight="bold">
            أهلاً بك في إدارة يا مسافر
          </MDTypography>
          <MDTypography variant="button" color="text">
            ابدأ من المهمة التي تريد إنجازها، وستبقى كل التغييرات مسودة حتى يتم نشرها.
          </MDTypography>
        </MDBox>
        {error && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            تعذر تحميل مؤشرات الإدارة؛ تظهر بيانات محدودة حتى نشر API الجديد.
          </Alert>
        )}
        <MDBox mb={4}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <MDBox>
              <MDTypography variant="h6" fontWeight="bold">
                مؤشرات النمو — آخر 30 يوماً
              </MDTypography>
              <MDTypography variant="caption" color="text">
                نشاط حقيقي داخل التطبيق، وليس مجرد عدد الحسابات المسجلة.
              </MDTypography>
            </MDBox>
            <Button
              component={Link}
              to="/analytics"
              color="success"
              endIcon={<Icon>arrow_back</Icon>}
            >
              عرض التحليلات
            </Button>
          </MDBox>
          {growth ? (
            <Grid container spacing={2}>
              {[
                ["تثبيتات جديدة", growth.kpis.newInstalls, false],
                ["مستخدمون نشطون", growth.kpis.activeInstallations, false],
                ["معدل التفعيل", growth.kpis.activationRate, true],
                ["إكمال التسجيل", growth.kpis.signupConversion, true],
                ["نقرات تجارية", growth.kpis.commercialClicks, false],
                ["الاحتفاظ D7", growth.kpis.d7Retention, true],
              ].map(([label, metric, percent]) => (
                <Grid item xs={6} md={4} lg={2} key={label}>
                  <Card
                    sx={{ p: 2, height: "100%", boxShadow: "none", border: "1px solid #E7EFEF" }}
                  >
                    <MDTypography variant="caption" color="text" display="block">
                      {label}
                    </MDTypography>
                    <MDTypography variant="h5" fontWeight="bold" color="dark" mt={0.5}>
                      {metric?.value == null
                        ? "—"
                        : percent
                        ? `${metric.value}%`
                        : new Intl.NumberFormat("ar").format(metric.value)}
                    </MDTypography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Alert severity="info">ستظهر مؤشرات النمو بعد نشر واجهة التحليلات الجديدة.</Alert>
          )}
        </MDBox>
        <MDBox mb={4} bgColor="white" borderRadius="xl" shadow="sm" p={3}>
          <MDTypography variant="h6" fontWeight="bold" mb={0.5}>
            ماذا تريد أن تفعل الآن؟
          </MDTypography>
          <MDTypography variant="button" color="text" display="block" mb={2.5}>
            اختصارات للمهام اليومية الأكثر استخداماً.
          </MDTypography>
          <Grid container spacing={2}>
            {quickActions.map((action) => (
              <Grid item xs={12} sm={6} lg={3} key={action.title}>
                <Button
                  component={Link}
                  to={action.to}
                  fullWidth
                  variant="outlined"
                  startIcon={<Icon>{action.icon}</Icon>}
                  sx={{
                    minHeight: "6.5rem",
                    justifyContent: "flex-start",
                    textAlign: "right",
                    color: "#12313F",
                    borderColor: "#D6E5E6",
                    px: 2,
                    "&:hover": { borderColor: "#179B82", backgroundColor: "#F2FBF8" },
                  }}
                >
                  <MDBox>
                    <MDTypography variant="button" fontWeight="bold" display="block">
                      {action.title}
                    </MDTypography>
                    <MDTypography variant="caption" color="text" sx={{ whiteSpace: "normal" }}>
                      {action.description}
                    </MDTypography>
                  </MDBox>
                </Button>
              </Grid>
            ))}
          </Grid>
        </MDBox>
        <Grid container spacing={3}>
          {cards.map(([title, count, color, icon, to]) => (
            <Grid item xs={12} sm={6} lg={4} key={title}>
              <Link to={to} style={{ textDecoration: "none" }}>
                <ComplexStatisticsCard
                  color={color}
                  icon={icon}
                  title={title}
                  count={count}
                  percentage={{ color: "success", amount: "", label: "عرض التفاصيل" }}
                />
              </Link>
            </Grid>
          ))}
        </Grid>
        <MDBox mt={4} bgColor="white" borderRadius="xl" shadow="sm" p={3}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <MDTypography variant="h6" fontWeight="bold">
              آخر العمليات
            </MDTypography>
            <Chip label="سجل التدقيق" color="success" variant="outlined" />
          </MDBox>
          {data.recentAudit.length === 0 ? (
            <MDTypography color="text" variant="button">
              لا توجد عمليات إدارية مسجلة بعد.
            </MDTypography>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>العملية</TableCell>
                  <TableCell>النوع</TableCell>
                  <TableCell>المستخدم</TableCell>
                  <TableCell>التاريخ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.recentAudit.map((item) => (
                  <TableRow key={item.auditId}>
                    <TableCell>{item.action}</TableCell>
                    <TableCell>{item.entityType}</TableCell>
                    <TableCell>{item.actor?.name || item.actor?.email}</TableCell>
                    <TableCell>{item.createdAt}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}

export default Dashboard;
